import React, { useState, useEffect } from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import apiClient from './api';
import moment from 'jalali-moment';
import {
    Container, Grid, Card, CardContent, Typography, Button, Box, Chip,
    Modal, TextField, Rating, Stack, Avatar, Paper, Alert, Collapse,
} from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import WorkHistoryIcon from '@mui/icons-material/WorkHistory';
import VerifiedIcon from '@mui/icons-material/Verified';
import CloseIcon from '@mui/icons-material/Close';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { formatToman } from './labels';
import { glass } from './theme';

const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: { xs: '92%', sm: 440 },
    maxHeight: '90vh',
    overflowY: 'auto',
    bgcolor: 'rgba(255,255,255,0.9)',
    backdropFilter: 'blur(18px)',
    border: '1px solid rgba(255,255,255,0.6)',
    borderRadius: '18px',
    boxShadow: '0 20px 60px rgba(15,51,43,0.25)',
    p: 4,
};

const REVIEW_QUESTIONS = [
    { key: 'quality_rating', label: 'کیفیت انجام کار و مراقبت' },
    { key: 'punctuality_rating', label: 'وقت‌شناسی و پایبندی به برنامه' },
    { key: 'behavior_rating', label: 'رفتار، احترام و ارتباط با خانواده' },
    { key: 'cleanliness_rating', label: 'رعایت نظافت و بهداشت' },
];

function MyRequests() {
    const location = useLocation();
    const navigate = useNavigate();
    const [showSubmitted, setShowSubmitted] = useState(!!location.state?.justSubmitted);
    const [myRequests, setMyRequests] = useState([]);
    const [message, setMessage] = useState('');
    const [openReviewModal, setOpenReviewModal] = useState(false);
    const [currentRequestForReview, setCurrentRequestForReview] = useState(null);
    const [ratings, setRatings] = useState({ quality_rating: 5, punctuality_rating: 5, behavior_rating: 5, cleanliness_rating: 5 });
    const [comment, setComment] = useState('');

    const fetchMyRequests = async () => {
        try {
            const response = await apiClient.get('/api/requests/');
            setMyRequests(response.data);
        } catch (error) {
            setMessage('برای مشاهده این صفحه باید به عنوان خانواده وارد شوید.');
        }
    };
    
    useEffect(() => {
        fetchMyRequests();
    }, []);

    const handleAcceptBid = async (requestId, bidId) => {
        try {
            await apiClient.post(`/api/requests/${requestId}/bids/${bidId}/accept/`);
            navigate(`/contracts/${requestId}`);
        } catch (error) {
            setMessage('خطا در پذیرش پیشنهاد.');
        }
    };

    const handleRejectBid = async (requestId, bidId) => {
        try {
            await apiClient.post(`/api/requests/${requestId}/bids/${bidId}/reject/`);
            setMessage('پیشنهاد رد شد.');
            fetchMyRequests();
        } catch (error) {
            setMessage('خطا در رد پیشنهاد.');
        }
    };

    const handleCompleteRequest = async (requestId) => {
        try {
            await apiClient.post(`/api/requests/${requestId}/complete/`);
            setMessage('خدمت با موفقیت تکمیل شد.');
            fetchMyRequests();
        } catch (error) {
            setMessage('خطا در تکمیل خدمت.');
        }
    };

    const handleOpenReviewModal = (request) => {
        setCurrentRequestForReview(request);
        setOpenReviewModal(true);
    };

    const handleCloseReviewModal = () => {
        setOpenReviewModal(false);
        setCurrentRequestForReview(null);
        setRatings({ quality_rating: 5, punctuality_rating: 5, behavior_rating: 5, cleanliness_rating: 5 });
        setComment('');
    };

    const handleReviewSubmit = async () => {
        try {
            await apiClient.post(`/api/requests/${currentRequestForReview.id}/review/`, {
                ...ratings,
                comment,
            });
            setMessage('نظر شما با موفقیت ثبت شد.');
            handleCloseReviewModal();
            fetchMyRequests();
        } catch (error) {
            setMessage('خطا در ثبت نظر.');
        }
    };

    const overallPreview = Math.round(
        (ratings.quality_rating + ratings.punctuality_rating + ratings.behavior_rating + ratings.cleanliness_rating) / 4
    );

    const getStatusChip = (status) => {
        if (status === 'pending') return <Chip label="در انتظار پیشنهاد" color="warning" variant="outlined" />;
        if (status === 'active') return <Chip label="فعال" color="success" />;
        if (status === 'completed') return <Chip label="تکمیل شده" color="primary" variant="outlined" />;
        return <Chip label={status} />;
    };

    return (
        <Box sx={{ background: glass.canvasGradient, minHeight: '100%', pb: 6 }}>
        <Container maxWidth="lg" sx={{ pt: 3 }}>
            <Button component={RouterLink} to="/dashboard" startIcon={<ArrowForwardIcon />} size="small" sx={{ mb: 1, color: 'text.secondary' }}>
                بازگشت به داشبورد
            </Button>
            <Typography variant="h4" component="h1" gutterBottom sx={{ textAlign: 'center', fontWeight: 800 }}>
                درخواست‌های من
            </Typography>
            <Collapse in={showSubmitted}>
                <Alert severity="success" sx={{ mb: 2, borderRadius: '12px' }} onClose={() => setShowSubmitted(false)}>
                    درخواست شما با موفقیت ثبت شد — به‌محض دریافت پیشنهاد از همیاران، همین‌جا نمایش داده می‌شود.
                </Alert>
            </Collapse>
            {message && <Typography color="primary" sx={{ textAlign: 'center', mb: 2 }}>{message}</Typography>}
            
            <Grid container spacing={3} justifyContent="center">
                {myRequests.map(req => (
                    <Grid item xs={12} md={8} key={req.id}>
                        <Card elevation={0} sx={glass.card}>
                            <CardContent>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                    <Typography variant="h6" component="h2">
                                        درخواست شماره #{req.id}
                                    </Typography>
                                    {getStatusChip(req.status)}
                                </Box>
                                <Typography color="text.secondary" gutterBottom>
                                    تاریخ شروع: {moment(req.start_date).locale('fa').format('YYYY/MM/DD')}
                                    {req.city ? ` · ${req.city}${req.district ? ` — ${req.district}` : ''}` : ''}
                                </Typography>
                                
                                <Typography variant="h6" sx={{ mt: 3, borderTop: '1px solid #eee', pt: 2 }}>
                                    همیاران پیشنهاد دهنده:
                                </Typography>
                                {(() => {
                                    const visibleBids = req.status === 'pending'
                                        ? (req.bids || []).filter((b) => b.status === 'submitted').sort((a, b) => (a.amount || Infinity) - (b.amount || Infinity))
                                        : (req.bids || []).filter((b) => b.status === 'accepted');
                                    if (visibleBids.length === 0) {
                                        return <Typography sx={{ mt: 1, fontStyle: 'italic' }}>هنوز هیچ پیشنهادی ثبت نشده است.</Typography>;
                                    }
                                    return (
                                        <Stack spacing={2} sx={{ mt: 2 }}>
                                            {req.status === 'pending' && visibleBids.length > 1 && (
                                                <Typography variant="caption" color="text.secondary">
                                                    بر اساس نرخ پیشنهادی، از ارزان به گران مرتب شده‌اند.
                                                </Typography>
                                            )}
                                            {visibleBids.map((bid, idx) => (
                                                <Paper key={bid.id} variant="outlined" sx={{
                                                    p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap',
                                                    borderRadius: '14px', transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                                                    '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 10px 26px rgba(15,51,43,0.12)' },
                                                }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                        <Avatar src={bid.provider.avatar} />
                                                        <Box>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                <Button component={RouterLink} to={`/providers/${bid.provider.user.id}`} sx={{ p: 0, minWidth: 0, justifyContent: 'flex-start' }}>
                                                                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>{bid.provider.full_name}</Typography>
                                                                </Button>
                                                                {bid.provider.is_verified_by_admin && <VerifiedIcon sx={{ fontSize: 16, color: 'primary.main' }} />}
                                                                {req.status === 'pending' && idx === 0 && visibleBids.length > 1 && (
                                                                    <Chip label="ارزان‌ترین" size="small" color="success" variant="outlined" />
                                                                )}
                                                            </Box>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                                                                <Chip label={formatToman(bid.amount)} size="small" color="primary" />
                                                                <Chip icon={<StarIcon />} label={bid.provider.average_rating.toFixed(1)} size="small" color="warning" />
                                                                <Chip icon={<WorkHistoryIcon />} label={`${bid.provider.completed_jobs_count} کار موفق`} size="small" />
                                                            </Box>
                                                        </Box>
                                                    </Box>
                                                    {req.status === 'pending' && (
                                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                                            <Button variant="contained" size="small" onClick={() => handleAcceptBid(req.id, bid.id)}>
                                                                انتخاب
                                                            </Button>
                                                            <Button variant="outlined" color="error" size="small" startIcon={<CloseIcon />} onClick={() => handleRejectBid(req.id, bid.id)}>
                                                                رد
                                                            </Button>
                                                        </Box>
                                                    )}
                                                </Paper>
                                            ))}
                                        </Stack>
                                    );
                                })()}
                                
                                <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
                                    {req.status === 'active' && (
                                        <Button variant="contained" color="success" onClick={() => handleCompleteRequest(req.id)}>
                                            علامت‌گذاری به عنوان "تکمیل شده"
                                        </Button>
                                    )}
                                    {req.status === 'completed' && !req.review && (
                                        <Button variant="outlined" onClick={() => handleOpenReviewModal(req)}>
                                            ثبت نظر و امتیاز
                                        </Button>
                                    )}
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            <Modal open={openReviewModal} onClose={handleCloseReviewModal}>
                <Box sx={modalStyle}>
                    <Typography variant="h6" component="h2" sx={{ fontWeight: 800 }}>ثبت نظر و امتیاز</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 1 }}>
                        به هر مورد جداگانه امتیاز دهید — امتیاز کلی از میانگین این چهار مورد محاسبه می‌شود.
                    </Typography>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        {REVIEW_QUESTIONS.map((q) => (
                            <Box key={q.key} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="body2">{q.label}</Typography>
                                <Rating
                                    value={ratings[q.key]}
                                    onChange={(event, newValue) => setRatings((r) => ({ ...r, [q.key]: newValue || 1 }))}
                                />
                            </Box>
                        ))}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 1, borderTop: '1px solid rgba(15,51,43,0.12)' }}>
                            <Typography sx={{ fontWeight: 700 }}>امتیاز کلی</Typography>
                            <Rating value={overallPreview} readOnly />
                        </Box>
                        <TextField
                            label="نظر شما (اختیاری)"
                            multiline
                            rows={3}
                            value={comment}
                            onChange={(e) => setComment(e.target.value)}
                            fullWidth
                        />
                        <Button onClick={handleReviewSubmit} variant="contained" size="large">ارسال نظر</Button>
                    </Stack>
                </Box>
            </Modal>
        </Container>
        </Box>
    );
}

export default MyRequests;

